// LT-314 — Auto-complete signals for the Dashboard checklist. Fetches the
// minimum data needed to detect that a task has been completed elsewhere:
//
//   connect_phone    ← user.phone from AuthContext (no fetch)
//   import_contacts  ← GET /contacts?limit=1 returns anything
//   create_campaign  ← GET /campaigns?limit=1 returns anything (LT-532)
//   invite_teammate  ← workspaceAPI.listMembers() returns > 1
//   review_billing   ← localStorage flag set when the user lands on
//                      /settings/billing
//
// All fetches happen once when the hook mounts (on the Dashboard). They're
// cheap because of the limit=1 query param and because the workspace
// members list is small by nature. If any request fails the flag stays
// false — dashboards shouldn't break on transient errors, the user can
// still dismiss the checklist manually.
import { useEffect, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { contactsAPI } from '../../services/contactsAPI';
import { campaignsAPI } from '../../services/campaignsAPI';
import { workspaceAPI } from '../../services/api';
import type { AutoCompleteSignals } from './useOnboardingChecklist';

export const BILLING_VIEWED_STORAGE_KEY = 'onboardingChecklist:v1:billingViewed';
export const TEAM_VIEWED_STORAGE_KEY = 'onboardingChecklist:v1:teamViewed';

function readBoolFlag(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * Setter helpers for the visited-flag pattern. Call from the target page
 * (SettingsBillingTab, SettingsHub team sub-route) on mount so the next
 * Dashboard render sees the task as complete.
 */
export function markBillingViewed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BILLING_VIEWED_STORAGE_KEY, '1');
  } catch {
    /* noop */
  }
}

export function markTeamViewed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TEAM_VIEWED_STORAGE_KEY, '1');
  } catch {
    /* noop */
  }
}

export function useChecklistSignals(): AutoCompleteSignals {
  const { user } = useAuth();
  const [hasContacts, setHasContacts] = useState(false);
  const [hasCampaigns, setHasCampaigns] = useState(false);
  const [teamSizeGreaterThanOne, setTeamSizeGreaterThanOne] = useState(false);

  // Visited-flag signals are read synchronously — no need for useEffect.
  // Reading on every render is fine because localStorage is O(1).
  const billingViewed = readBoolFlag(BILLING_VIEWED_STORAGE_KEY);
  const teamViewed = readBoolFlag(TEAM_VIEWED_STORAGE_KEY);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    // One-shot probes. limit=1 keeps the response tiny. Errors silently
    // leave the flag false — the dashboard stays usable.
    contactsAPI
      .list({ limit: 1 })
      .then((rows) => {
        if (!cancelled) setHasContacts(Array.isArray(rows) && rows.length > 0);
      })
      .catch(() => {
        /* noop */
      });

    campaignsAPI
      .list({ limit: 1 })
      .then((rows) => {
        if (!cancelled) setHasCampaigns(Array.isArray(rows) && rows.length > 0);
      })
      .catch(() => {
        /* noop */
      });

    workspaceAPI
      .listMembers()
      .then((rows) => {
        if (!cancelled) setTeamSizeGreaterThanOne(Array.isArray(rows) && rows.length > 1);
      })
      .catch(() => {
        /* noop */
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return {
    phoneSet: Boolean(user?.phone),
    hasContacts,
    hasCampaigns,
    // Either a fetched signal OR the visited-flag marks teammate-task done.
    // The visited-flag exists because admins who hand-invite via a CLI /
    // DB fixture still want the task green after they've seen the panel.
    teamSizeGreaterThanOne: teamSizeGreaterThanOne || teamViewed,
    billingViewed,
  };
}
